import React, { Key, useCallback, useEffect, useState } from 'react';
import RevxTable, { TableColumnsType } from 'components/RevxTable';
import { useAppDispatch, useAppSelector } from 'app/hooks';
import { useTranslation } from 'react-i18next';
import { Popconfirm, Space, Switch, Tag } from 'antd';
import { RevxButton } from 'components/RevxButton';
import {
  getSkuInheritSetting,
  InheritSettingEnum,
  MultiTypeDisplay,
  PartialSKU,
  SKU,
} from 'domains/skuDomain';
import { useOrg, useOrgId } from 'hooks/useOrg';
import { LoadingOutlined, QuestionCircleOutlined } from '@ant-design/icons';
import { isPlatformOrg, OrgSkuInheritSettingEnum } from 'domains/orgDomain';
import { FilterOperatorEnum } from 'domains/appDomain';
import { PagerState, SKU_NOT_DRAFT_FILTER } from 'models/skuModel';
import skuService from 'services/skuService';
import { RevxSpin } from 'components/RevxSpin';
import { success } from 'components/RevxMessage';
import { useHistory } from 'react-router-dom';
import { routesMap } from 'app/Router';

type Props = {
  isInherit?: boolean;
  selectedSkuId?: number | string;
  tenantId?: number;
  pagerStateProp:
    | 'defaultPager'
    | 'inheritBasePager'
    | 'inheritParentPager'
    | 'inheritCurrentPager';
  onSelectedChange?: (id?: number | string) => void;
};
export const SkuTable = ({
  isInherit,
  selectedSkuId,
  tenantId,
  pagerStateProp,
  onSelectedChange,
}: Props) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const history = useHistory();
  const loading = useAppSelector((state) => state.loading.effects.sku.getSKUs);
  const pagedData = useAppSelector((state) => state.sku.pagedData);
  const pager = useAppSelector((state) => {
    const pagerMap = {
      defaultPager: state.sku.defaultPager,
      inheritBasePager: state.sku.inheritBasePager,
      inheritParentPager: state.sku.inheritParentPager,
      inheritCurrentPager: state.sku.inheritCurrentPager,
    };

    return pagerMap[pagerStateProp];
  });
  const curItem = useAppSelector((state) => state.sku.curItem);
  const skuTesterVisible = useAppSelector(
    (state) => state.sku.skuTesterState.visible
  );
  const filterObj = useAppSelector((state) => state.sku.filterObj);
  const org = useOrg();
  const orgId = useOrgId();
  const [editing, setEditing] = useState(false);

  const canEditSku = (sku: SKU) => {
    if (isInherit) {
      return false;
    }

    const isBaseOrg = isPlatformOrg({
      id: orgId,
    });

    if (!isBaseOrg && sku.isBaseSku) {
      return false;
    }

    return true;
  };

  const canEditUnitPrice = (sku: SKU) => {
    return !(orgId !== 1 && sku.isBaseSku);
  };

  const columns: TableColumnsType<SKU> = [
    {
      title: t('platform.sku.columnTitle.isBase'),
      dataIndex: 'isBaseSku',
      render(isBaseSku: boolean) {
        return isBaseSku
          ? t('platform.sku.base')
          : t('platform.sku.organization');
      },
    },
    {
      title: t('common.id'),
      width: 60,
      dataIndex: 'id',
    },
    {
      title: t('platform.sku.columnTitle.skuType'),
      dataIndex: 'skuType',
      render(text, item: SKU) {
        return item?.skuType?.name;
      },
    },
    {
      title: t('common.name'),
      dataIndex: 'name',
    },
    {
      title: t('platform.sku.columnTitle.autoInherit'),
      dataIndex: 'inheritSetting',
      render(text: InheritSettingEnum, sku: SKU) {
        if (org?.skuInheritSetting) {
          return getSkuInheritSetting(sku, org?.skuInheritSetting) ===
            OrgSkuInheritSettingEnum.AUTO
            ? t('common.yes')
            : t('common.no');
        } else {
          return <LoadingOutlined />;
        }
      },
    },
    {
      title: t('platform.sku.columnTitle.standaloneOrMulti'),
      dataIndex: 'multiType',
      render(text: string) {
        return MultiTypeDisplay[text];
      },
    },
    {
      title: t('common.updatedAt'),
      dataIndex: 'updateTime',
      valueType: 'time',
    },
    {
      title: t('platform.sku.columnTitle.isEditing'),
      dataIndex: 'editing',
      render(editing) {
        return editing ? t('common.yes') : t('common.no');
      },
    },
    {
      title: t('common.active'),
      dataIndex: 'active',
      render: (active, record: SKU) =>
        record.baseRefId ? (
          <Switch
            defaultChecked={record.active}
            onChange={(val) => {
              dispatch.sku.updateSkuActiveStatus({
                skuId: record.id,
                activeStatus: val,
              });
            }}
            title="Active"
            size="small"
          ></Switch>
        ) : (
          <Tag color={active ? 'green' : 'volcano'}>
            {active ? t('common.active') : t('common.inactive')}
          </Tag>
        ),
    },
    {
      title: t('common.actions'),
      key: 'actions',
      align: 'left',
      width: 180,
      render: (value, record) => (
        <Space size="small">
          <RevxButton
            type="link"
            key="view"
            size="small"
            onClick={async () => {
              setEditing(true);
              const skuDraft = await skuService.startViewSku(record, true);

              dispatch.sku.setState({
                curItem: { ...skuDraft, readonly: true },
              });

              setEditing(false);
            }}
          >
            {t('common.view')}
          </RevxButton>
          {canEditSku(record) && (
            <RevxButton
              type="link"
              key="edit"
              size="small"
              onClick={async () => {
                setEditing(true);
                const skuDraft = await skuService.startEditSku(record);
                dispatch.sku.setState({ curItem: skuDraft });
                setEditing(false);
              }}
            >
              {t('common.edit')}
            </RevxButton>
          )}
          {canEditUnitPrice(record) && (
            <RevxButton
              type="link"
              key="edit"
              size="small"
              onClick={async () => {
                setEditing(true);
                const skuDraft = await skuService.startViewSku(record, true);
                history.push(
                  `${routesMap.platform.path}/sku/unit-price?skuId=${record.id}&name=${skuDraft.name}&page=${pager.currentPage}`
                );
                setEditing(false);
              }}
            >
              Unit Price
            </RevxButton>
          )}

          {/* <RevxButton
            type="link"
            key="test"
            size="small"
            onClick={() => {
              dispatch.sku.setSkuTesterItem(record);
              dispatch.sku.setSkuTesterVisible(true);
            }}
          >
            {t('platform.sku.test')}
          </RevxButton> */}
          {canEditSku(record) && (
            <Popconfirm
              placement="topRight"
              title={t('common.confirmQuestion')}
              icon={<QuestionCircleOutlined style={{ color: 'red' }} />}
              onConfirm={async () => {
                await dispatch.sku.removeSku(record);
                success(t('platform.sku.deleteSkuSuccessMsg'));
              }}
              okText={t('common.ok')}
              cancelText={t('common.cancel')}
            >
              <RevxButton type="link" key="test" size="small">
                {t('platform.sku.partsTable.button.remove')}
              </RevxButton>
            </Popconfirm>
          )}
        </Space>
      ),
    },
  ];

  const onTableChange = useCallback(
    (pagination, filters, sorter, extra) => {
      switch (extra.action) {
        case 'paginate':
          if (pagination.pageSize !== pager.pageSize) {
            const newPager: PagerState = {
              pageSize: pagination.pageSize,
              currentPage: 1,
            };

            dispatch.sku.setState({
              [pagerStateProp]: newPager,
            });
          }

          if (pagination.current !== pager.currentPage) {
            const newPager: PagerState = {
              pageSize: pagination.pageSize,
              currentPage: pagination.current,
            };
            dispatch.sku.setState({
              [pagerStateProp]: newPager,
            });
          }

          onSelectedChange && onSelectedChange(undefined);
          break;
      }
    },
    [
      dispatch.sku,
      onSelectedChange,
      pager.currentPage,
      pager.pageSize,
      pagerStateProp,
    ]
  );

  useEffect(() => {
    dispatch.sku.getSKUs({
      page: pager,
      query: {
        ...filterObj,
        ...(isInherit
          ? {
              isBaseSku: {
                operator: FilterOperatorEnum.EQUALS,
                values: [
                  isPlatformOrg({ id: tenantId || orgId }) ? true : false,
                ],
              },
            }
          : {}),
        draft: SKU_NOT_DRAFT_FILTER,
      },
      tenantId,
    });
  }, [dispatch.sku, filterObj, isInherit, orgId, pager, tenantId]);

  return (
    <div hidden={!!curItem || skuTesterVisible}>
      <RevxSpin spinning={editing}>
        <RevxTable<SKU>
          loading={loading}
          columns={columns}
          rowSelection={
            isInherit
              ? {
                  type: 'radio',
                  selectedRowKeys:
                    selectedSkuId === undefined ? [] : [selectedSkuId],
                  onChange(selectedRowKeys: Key[]) {
                    if (onSelectedChange) {
                      onSelectedChange(
                        selectedRowKeys[0] ? selectedRowKeys[0] : undefined
                      );
                    }
                  },
                }
              : undefined
          }
          size="small"
          pagination={{
            pageSize: pager.pageSize,
            current: pager.currentPage,
            total: pagedData?.totalElements,
            showTotal: (total) => t('common.pagerTotal', { total: total }),
          }}
          rowClassName={(record: PartialSKU) => {
            if (curItem?.id === record.id) {
              return 'editing';
            }
            return '';
          }}
          className={isInherit ? 'sku-inherit-table' : ''}
          indentSize={20}
          dataSource={pagedData?.content || []}
          onChange={onTableChange}
          onRow={(record) => ({
            onClick: () => {
              onSelectedChange && onSelectedChange(record.id);
            },
          })}
        />
      </RevxSpin>
    </div>
  );
};
